import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { Icon } from './icons';
import { cx, IconButton, type Tone } from './primitives';
export interface ModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    dismissible?: boolean;
}
/** Native dialog supplies modal focus containment and Escape handling. State stays controlled. */
export function Modal({ open, onOpenChange, title, description, children, footer, size = 'md', className, dismissible = true }: ModalProps) {
    const ref = useRef<HTMLDialogElement>(null);
    const restore = useRef<HTMLElement | null>(null);
    const id = useId();
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        if (open && !el.open) {
            restore.current = document.activeElement as HTMLElement;
            el.showModal();
        }
        if (!open && el.open) {
            el.close();
            restore.current?.focus();
        }
    }, [open]);
    useEffect(() => {
        const dialog = ref.current;
        return () => {
            if (dialog?.open) dialog.close();
            if (restore.current?.isConnected) restore.current.focus();
        };
    }, []);
    return <dialog ref={ref} className={cx('bs-modal', `bs-modal--${size}`, className)} aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-desc` : undefined} onCancel={e => { e.preventDefault(); if (dismissible)
        onOpenChange(false); }} onClick={e => { if (!dismissible)
        return; const box = ref.current?.getBoundingClientRect(); if (e.target === ref.current && box && (e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom))
        onOpenChange(false); }}><div className="bs-modal-header"><div><span className="bs-eyebrow">FRAMEON STUDIO</span><h2 id={`${id}-title`}>{title}</h2>{description && <p id={`${id}-desc`}>{description}</p>}</div>{dismissible && <IconButton label="Close dialog" icon="close" variant="ghost" onClick={() => onOpenChange(false)}/>}</div><div className="bs-modal-body">{children}</div>{footer && <div className="bs-modal-footer">{footer}</div>}</dialog>;
}
export function Drawer(props: ModalProps) { return <Modal {...props} className={cx('bs-drawer', props.className)}/>; }
export interface ToastMessage {
    title: string;
    description?: string;
    tone?: Tone;
    duration?: number;
}
interface ToastRecord extends ToastMessage {
    id: number;
}
const ToastContext = createContext<((message: ToastMessage) => void) | null>(null);
export function ToastProvider({ children }: {
    children: React.ReactNode;
}) {
    const [items, setItems] = useState<ToastRecord[]>([]);
    const next = useRef(0);
    const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
    const dismiss = useCallback((id: number) => setItems(xs => xs.filter(x => x.id !== id)), []);
    const notify = useCallback((m: ToastMessage) => { const id = ++next.current; setItems(xs => [...xs, m && { ...m, id }].slice(-4)); if (m.duration !== 0)
        timers.current.push(setTimeout(() => dismiss(id), m.duration ?? 5500)); }, [dismiss]);
    useEffect(() => () => timers.current.forEach(clearTimeout), []);
    return <ToastContext.Provider value={notify}>{children}<div className="bs-theme bs-toast-stack" aria-live="polite" aria-relevant="additions">{items.map(t => <div className={cx('bs-toast', `bs-tone-${t.tone || 'info'}`)} key={t.id}><Icon name={t.tone === 'success' ? 'check' : t.tone === 'danger' ? 'alert' : 'info'}/><div><strong>{t.title}</strong>{t.description && <p>{t.description}</p>}</div><IconButton icon="close" label={`Dismiss ${t.title}`} size="sm" variant="ghost" onClick={() => dismiss(t.id)}/></div>)}</div></ToastContext.Provider>;
}
export function useToast() { const context = useContext(ToastContext); if (!context)
    throw new Error('Wrap your app in ToastProvider before calling useToast.'); return context; }
